import { t } from "@lingui/macro"
import {
    Stack,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormLabel,
    SelectChangeEvent,
} from "@mui/material"
import { observer } from "mobx-react"

import { AccessDetailStore } from "./store"

import { Type } from "./form-fields/Type"

import { FormFieldSet } from "src/components/FormFieldSet"
import { useStore } from "src/store/lib/useStore"

export const ResourceSection = observer(() => {
    const store = useStore(AccessDetailStore)

    const onNameTextChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const value = event.target.value.trim() !== "" ? event.target.value : ""
        store.formFields.set("name", value)
    }

    const onExternalIdTextChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const value = event.target.value.trim() !== "" ? event.target.value : ""
        store.formFields.set("externalId", value)
    }

    const onIntegrationModeChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const mode = event.target.value
        store.formFields.set("integrationMode", mode)

        // Clear fields when switching modes
        store.formFields.set("externalId", "")
        store.formFields.set("integrationConnectorId", 0)
        store.formFields.set("integrationType", "")
    }

    const onIntegrationConnectorChange = (event: SelectChangeEvent<string>) => {
        const connectorId = Number(event.target.value)
        store.formFields.set("integrationConnectorId", connectorId)

        // Find the selected connector and set the integration type
        const selectedConnector = store.integrationConnectors.find(
            (connector) => connector.integration_id === connectorId,
        )
        if (selectedConnector !== undefined && selectedConnector !== null) {
            store.formFields.set(
                "integrationType",
                selectedConnector.type ?? "",
            )
        }
    }

    // Determine the label for the external ID field based on integration type
    const getExternalIdLabel = () => {
        const mode = store.formFields.get("integrationMode")
        if (mode === "parakey") {
            return t`access-detail-modal.resource-section.parakey-resource-id-label`
        }

        const integrationType = store.formFields
            .get("integrationType")
            ?.toLowerCase()
        if (integrationType === "accessy") {
            return t`access-detail-modal.resource-section.accessy-resource-id-label`
        }
        return t`access-detail-modal.resource-section.external-id-label`
    }

    const integrationMode = store.formFields.get("integrationMode") ?? "parakey"

    return (
        <FormFieldSet
            header={t`access-detail-modal.resource-section.field-set-header`}
        >
            <Stack spacing={2}>
                <TextField
                    label={t`access-detail-modal.resource-section.name-label`}
                    value={store.formFields.get("name")}
                    onChange={onNameTextChange}
                    helperText={store.formFields.error("name")}
                    error={Boolean(store.formFields.error("name"))}
                />
                <Type />

                <FormControl component="fieldset">
                    <FormLabel component="legend">
                        {t`access-detail-modal.resource-section.integration-mode-label`}
                    </FormLabel>
                    <RadioGroup
                        value={integrationMode}
                        onChange={onIntegrationModeChange}
                    >
                        <FormControlLabel
                            value="parakey"
                            control={<Radio />}
                            label={t`access-detail-modal.resource-section.parakey-mode-label`}
                        />
                        <FormControlLabel
                            value="connector"
                            control={<Radio />}
                            label={t`access-detail-modal.resource-section.connector-mode-label`}
                        />
                    </RadioGroup>
                </FormControl>

                {integrationMode === "connector" && (
                    <FormControl
                        fullWidth
                        error={Boolean(
                            store.formFields.error("integrationConnectorId"),
                        )}
                    >
                        <InputLabel id="integration-connector-label">
                            {t`access-detail-modal.resource-section.integration-connector-label`}
                        </InputLabel>
                        <Select
                            labelId="integration-connector-label"
                            value={
                                store.formFields.get(
                                    "integrationConnectorId",
                                ) !== undefined
                                    ? String(
                                          store.formFields.get(
                                              "integrationConnectorId",
                                          ),
                                      )
                                    : ""
                            }
                            onChange={onIntegrationConnectorChange}
                            label={t`access-detail-modal.resource-section.integration-connector-label`}
                        >
                            {store.integrationConnectors.map((connector) => (
                                <MenuItem
                                    key={connector.integration_id}
                                    value={connector.integration_id}
                                >
                                    {connector.name} ({connector.type})
                                </MenuItem>
                            ))}
                        </Select>
                        {Boolean(
                            store.formFields.error("integrationConnectorId"),
                        ) && (
                            <FormHelperText>
                                {store.formFields.error(
                                    "integrationConnectorId",
                                )}
                            </FormHelperText>
                        )}
                    </FormControl>
                )}

                <TextField
                    label={getExternalIdLabel()}
                    value={store.formFields.get("externalId")}
                    onChange={onExternalIdTextChange}
                    helperText={store.formFields.error("externalId")}
                    error={Boolean(store.formFields.error("externalId"))}
                />
            </Stack>
        </FormFieldSet>
    )
})
